import {
  ACTIVE_MATCH_MODE,
  AI,
  FIELD,
  fieldPlayersPerTeam,
  goalMouth,
  isInOffsidePosition,
  offsideSpaceScore,
  KICK,
  type Ball,
  type Player,
  type ShotStyle,
  type TeamSide,
  type TacticalRole,
  type Vec2,
  playerRoleProfile,
  TACTICS,
} from "@golazo/shared";
import { clamp, vec, len2 } from "../core/math.js";
// Determinista entre motores: ver `core/detMath.ts`.
import { dacos, datan2, dcos, dlog1p, dsin } from "../core/detMath.js";
import type { Random } from "../core/random.js";
import { signed } from "../core/random.js";
import { defaultCrossTarget, isInCrossingZone } from "../game/kick.js";
import {
  passAngularError,
  scoreThroughPass,
} from "./passPhysics.js";
import type { TeamTacticsState } from "./tactics.js";
import { AI_SKILL, bodyScale, decisionSlack, readScale, seesMate } from "./skill.js";

const HALF_HEIGHT = FIELD.HEIGHT / 2;

export interface ShotEval {
  score: number;
  target: Vec2;
  clearance: number;
  power: number;
  style: ShotStyle;
}

export interface PassEval {
  score: number;
  target: Vec2;
  mate: Player;
  power: number;
  backward: boolean;
  /** Pase al hueco (through ball). */
  through?: boolean;
  /** Pase a la pared: se apunta a la banda y el rebote hace el resto. */
  wall?: boolean;
}

export interface CrossEval {
  score: number;
  target: Vec2;
  mate: Player;
  power: number;
  /** Error de timing en segundos (0 = perfecto). */
  timingError: number;
}

export type AttackChoice = {
  kind: "shoot" | "pass" | "cross" | "dribble" | "fake";
  target: Vec2;
  power: number;
  spread: number;
  shotStyle?: ShotStyle;
};

function dist(a: Vec2, b: Vec2): number {
  return len2(a.x - b.x, a.y - b.y);
}

/** ¿Esta este jugador dentro del area que ataca? */
function inAttackingBox(p: Player): boolean {
  return pointInAttackingBox(p.pos, p.side);
}

function pointInAttackingBox(pos: Vec2, side: TeamSide): boolean {
  const half = FIELD.BOX_HEIGHT / 2;
  if (Math.abs(pos.y - HALF_HEIGHT) > half) return false;
  return side === "home" ? pos.x >= FIELD.WIDTH - FIELD.BOX_WIDTH : pos.x <= FIELD.BOX_WIDTH;
}

/**
 * Donde estara en `AI.CROSS_ARRIVAL_LEAD` segundos si sigue corriendo: el
 * centro se pone al espacio al que llega el rematador, no a donde esta.
 */
function arrivingAt(mate: Player): Vec2 {
  return vec(mate.pos.x + mate.vel.x * AI.CROSS_ARRIVAL_LEAD, mate.pos.y + mate.vel.y * AI.CROSS_ARRIVAL_LEAD);
}

function attackedGoal(side: TeamSide): Vec2 {
  return vec(side === "home" ? FIELD.WIDTH : 0, HALF_HEIGHT);
}

function attackDir(side: TeamSide): number {
  return side === "home" ? 1 : -1;
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq < 1e-6) return dist(p, a);

  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq, 0, 1);
  return len2(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

export function laneClearance(from: Vec2, to: Vec2, opponents: readonly Player[]): number {
  let clear = Infinity;
  for (const o of opponents) {
    clear = Math.min(clear, distanceToSegment(o.pos, from, to));
  }
  return clear;
}

/** `laneClearance` sin los rivales que quedan por detras del origen del pase. */
function laneClearanceAhead(from: Vec2, to: Vec2, opponents: readonly Player[]): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  let clear = Infinity;
  for (const o of opponents) {
    if ((o.pos.x - from.x) * dx + (o.pos.y - from.y) * dy <= 0) continue;
    clear = Math.min(clear, distanceToSegment(o.pos, from, to));
  }
  return clear;
}

function nearestOpponentDist(pos: Vec2, opponents: readonly Player[]): number {
  let best = Infinity;
  for (const o of opponents) {
    best = Math.min(best, dist(pos, o.pos));
  }
  return best;
}

function lerpPressure(t: number): number {
  return 0.42 + t * 0.58;
}

function shootRange(): number {
  return AI.SHOOT_RANGE * ACTIVE_MATCH_MODE.ai.shootRangeScale;
}

function closeShotRange(): number {
  return AI.CLOSE_SHOT_RANGE * (0.85 + ACTIVE_MATCH_MODE.ai.shootRangeScale * 0.15);
}

/** Tantea la boca de la porteria y puntua cada hueco. Elige estilo de remate. */
export function evaluateShot(
  player: Player,
  ball: Ball,
  opponents: readonly Player[],
  pressureDist: number,
): ShotEval {
  const profile = ACTIVE_MATCH_MODE.ai;
  const goal = attackedGoal(player.side);
  const goalX = goal.x;
  const usable = Math.max(goalMouth().half - AI.SHOT_POST_MARGIN, 12);
  const toGoalLine = Math.abs(goal.x - player.pos.x);
  const toGoal = dist(player.pos, goal);
  const range = shootRange();
  const close = closeShotRange();

  let target = vec(goalX, HALF_HEIGHT);
  let clearance = -1;

  for (let i = 0; i < AI.SHOT_SAMPLES; i++) {
    const t = (i / (AI.SHOT_SAMPLES - 1)) * 2 - 1;
    const candidate = vec(goalX, HALF_HEIGHT + t * usable);
    const clear = laneClearance(ball.pos, candidate, opponents);
    if (clear > clearance) {
      clearance = clear;
      target = candidate;
    }
  }

  const clearNorm = clamp(clearance / AI.SHOT_CLEAR_SCALE, 0, 1.2);
  // Ver SHOT_DIST_FALLOFF/FLOOR: la caida antigua dejaba el tiro lejano por
  // debajo del umbral hiciera lo que hiciera el jugador.
  const distNorm =
    toGoalLine < close
      ? 1.2
      : clamp(
          1 - ((toGoal - close) / (range - close)) * AI.SHOT_DIST_FALLOFF,
          AI.SHOT_DIST_FLOOR,
          1,
        );

  let score = clearNorm * distNorm * 100 * profile.shootBias;

  // En modos arcade, un carril mediocre sigue siendo buen tiro.
  if (profile.shotThresholdScale < 0.8) {
    score += distNorm * 22;
    if (toGoalLine < range * 0.7) score += 18;
  }

  if (pressureDist < AI.PRESSURE_RADIUS) {
    // Bajo presion cerca del area: mejor chutar que regalar el balon.
    if (toGoalLine < close * 1.15) score *= 1.15;
    else score *= lerpPressure(pressureDist / AI.PRESSURE_RADIUS);
  }

  if (toGoalLine < close && clearance > AI.LANE_CLEARANCE * 0.25) {
    score = Math.max(score, 60 + clearNorm * 35);
  }

  // 1v1 / duelos: si hay vision de porteria, el score sube fuerte.
  const outfieldOpponents = opponents.filter((o) => o.role !== "gk");
  if (outfieldOpponents.length <= 2 && toGoalLine < range) {
    score = Math.max(score, 48 + distNorm * 40);
  }

  const power =
    toGoalLine < close
      ? clamp(0.58 + clearNorm * 0.35, 0.55, 0.92)
      : clamp(toGoal / range, 0.55, 1);

  const gk = opponents.find((o) => o.role === "gk");
  const gkDepth = gk ? Math.abs(gk.pos.x - goalX) : 0;
  const gkRushing =
    gkDepth > AI.CHIP_GK_RUSH_DEPTH && toGoalLine < AI.CHIP_MAX_RANGE;
  const oneVone =
    outfieldOpponents.filter((o) => dist(o.pos, player.pos) < 160).length <= 1;

  let style: ShotStyle = "power";

  // Vaselina: portero achicando en mano a mano (no cambia el score base salvo bonus).
  if (gkRushing && oneVone && toGoalLine < close * 1.15) {
    style = "chip";
    score += AI.CHIP_SCORE_BONUS;
    return { score, target: vec(goalX, HALF_HEIGHT), clearance, power: AI.CHIP_POWER, style };
  }
  if (player.trapTime > 0 || ball.loft > 18) {
    style = "volley";
  } else if (
    toGoalLine < close * 1.1 &&
    clearNorm > 0.7 &&
    pressureDist > AI.PRESSURE_RADIUS * 0.8
  ) {
    style = "placed";
  } else if (toGoalLine > close * 1.2 && clearNorm > 0.45) {
    style = "power";
  }

  return {
    score,
    target,
    clearance,
    power: style === "placed" ? AI.PLACED_POWER : power,
    style,
  };
}

/**
 * Lleva un score de pase/centro a la MISMA escala que el de tiro (~0-120).
 *
 * `evaluateShot` es un producto de factores acotados (`clearNorm` y `distNorm`
 * no pasan de 1,2), asi que vive en 0-120 por construccion. `evaluatePass` es
 * un producto de multiplicadores SIN TECHO — `advance/120` vale 3 o mas, el
 * carril libre no satura, y encima se apilan media docena de bonus — asi que
 * se dispara. Medido sobre 8 partidos por modo:
 *
 *   tiro   p50 11-27    p95 70-140    max 189
 *   pase   p50 662-3593 p95 6590-83316 max 112.446
 *
 * Consecuencia: TODA comparacion del tipo `pass.score > shot.score * bias` la
 * ganaba el pase por dos ordenes de magnitud, pasara lo que pasara. Los unicos
 * remates que sobrevivian eran los de umbral absoluto colocados antes de esas
 * comparaciones, y de ahi salia que el 100% de los tiros fueran desde el area.
 *
 * El logaritmo es lo que hace el trabajo: comprime cinco ordenes de magnitud a
 * uno solo y **es monotono creciente**, asi que el mejor pase sigue siendo
 * exactamente el mismo. Lo unico que cambia es como compite contra el tiro.
 *
 * Constantes calibradas para alinear percentiles con el tiro: p50 → ~15 y
 * p95 → ~70, que son los del tiro medidos arriba.
 */
export function normalizeActionScore(raw: number): number {
  if (raw <= 0) return 0;
  return AI.ACTION_SCORE_GAIN * dlog1p(raw / AI.ACTION_SCORE_PIVOT);
}

/**
 * Lo que ABRE un pase, no lo que avanza.
 *
 * Un pase hacia atras siempre puntua mal si se mide por lo que adelanta: por
 * definicion adelanta negativo. Pero en construccion es justo al reves — se
 * juega atras para que el que recibe tenga campo delante y pueda cambiar el
 * lado. Medido antes de esto: solo el 2% de los pases iban hacia atras, o sea
 * que la IA no construia, solo empujaba.
 *
 * Se valoran las dos cosas que hacen bueno un pase atras:
 *
 *  1. **Que el receptor este libre.** Devolversela a alguien presionado no es
 *     construir, es pasarle el problema.
 *  2. **Que tenga SALIDAS.** Companeros por delante de el a los que pueda
 *     jugar limpio. Un central libre pero sin nadie a quien darsela no ha
 *     abierto nada.
 *
 * Devuelve un multiplicador (1 = no abre nada).
 */
function buildUpValue(
  mate: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
  dir: number,
): number {
  const room = nearestOpponentDist(mate.pos, opponents);
  // Sin espacio no hay construccion posible: el pase solo traslada la presion.
  if (room < AI.PRESSURE_RADIUS) return AI.BUILDUP_CROWDED_SCALE;

  let outlets = 0;
  for (const m of mates) {
    if (m.id === mate.id || m.role === "gk") continue;
    // Por delante del receptor, no a su altura: la salida tiene que progresar.
    if ((m.pos.x - mate.pos.x) * dir < AI.BUILDUP_OUTLET_AHEAD) continue;
    if (laneClearance(mate.pos, m.pos, opponents) < AI.LANE_CLEARANCE * 0.8) continue;
    outlets++;
  }

  const space = clamp(room / AI.FREE_RADIUS, 0, 1.4);
  return 1 + space * outlets * AI.BUILDUP_OUTLET_GAIN;
}

/**
 * Pases directos (instruccion `directness`): por encima de 0,5 premia el pase
 * LARGO hacia delante y castiga el corto; por debajo, al reves. En 0,5 —el
 * plan de fabrica, o sea el online y el Partido rapido— devuelve 1 exacto.
 * Un pase largo hacia atras no cuenta como directo.
 */
function directnessMul(directness: number, passDist: number, forward: number): number {
  const lean = (directness - 0.5) * 2;
  if (lean === 0) return 1;
  const longness = clamp(
    (passDist - AI.SHORT_PASS_MAX) / (AI.DIRECT_LONG_PASS - AI.SHORT_PASS_MAX),
    0,
    1,
  );
  const direct = forward > 0 ? longness : 0;
  return 1 + lean * AI.DIRECTNESS_GAIN * (direct * 2 - 1);
}

function opennessScore(openness: number): number {
  if (openness >= AI.FREE_RADIUS) return AI.PASS_FREE_BONUS * (1 + (openness - AI.FREE_RADIUS) / 180);
  if (openness <= AI.MARKED_RADIUS) {
    return AI.PASS_MARKED_PENALTY * clamp(openness / AI.MARKED_RADIUS, 0.2, 1);
  }
  const t = (openness - AI.MARKED_RADIUS) / (AI.FREE_RADIUS - AI.MARKED_RADIUS);
  return AI.PASS_MARKED_PENALTY + t * (1 - AI.PASS_MARKED_PENALTY);
}

function considerPass(
  best: PassEval | null,
  candidate: PassEval,
): PassEval {
  if (!best || candidate.score > best.score) return candidate;
  return best;
}

/**
 * El mejor pase de CONSTRUCCION de la ultima evaluacion, aparte del mejor
 * absoluto.
 *
 * `evaluatePass` devuelve un solo ganador, y hacia delante casi siempre gana:
 * eso hacia que un pase atras no llegara nunca a ser una opcion que considerar
 * — la rama de construccion no veia ninguno. Guardarlo aparte permite decidir
 * entre "progresar" y "construir" en vez de que lo segundo ni aparezca.
 */
let lastBackward: PassEval | null = null;

/** El mejor pase atras de la ultima llamada a `evaluatePass`, con la puntuacion normalizada. */
export function lastBuildUpPass(): PassEval | null {
  return lastBackward && { ...lastBackward, score: normalizeActionScore(lastBackward.score) };
}

/**
 * El pase mas SEGURO de la ultima evaluacion (ver `AI.RECYCLE_*`), vaya hacia
 * donde vaya: el que usa `chooseAttack` para reciclar cuando le aprietan y lo
 * que tocaba era conducir contra el rival.
 */
let lastSafe: { pass: PassEval; safety: number } | null = null;

export function lastRecyclePass(): PassEval | null {
  return lastSafe?.pass ?? null;
}

/** Busca el mejor pase directo o a pared, priorizando companeros libres. */
export function evaluatePass(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
  team?: TeamTacticsState,
  /** Audacia del pasador: >1 intenta carriles mas cerrados. 1 = referencia. */
  boldness = 1,
): PassEval | null {
  const profile = ACTIVE_MATCH_MODE.ai;
  lastBackward = null;
  lastSafe = null;
  const goal = attackedGoal(player.side);
  const myAdvance = dist(player.pos, goal);
  const pressured = opponents.some((o) => dist(o.pos, player.pos) < AI.PRESSURE_RADIUS);
  const ballSide = Math.sign(ball.pos.y - HALF_HEIGHT);
  const dir = attackDir(player.side);
  const tempo = team?.instructions.tempo ?? 0.5;
  const directness = team?.instructions.directness ?? 0.5;
  // Contraataque: recien robado, un equipo que deja a uno arriba la busca ya.
  const counter =
    team && team.counterLeft > 0 ? clamp(team.instructions.outlet, 0, 1) * TACTICS.COUNTER_FORWARD_GAIN : 0;
  // El torpe elige peor (`decisionSlack`): no ve a todos, no ve el carril, y
  // el pase al hueco y la pared, que son lecturas de jugador hecho, casi no.
  const slack = decisionSlack(player);
  const craft = 1 - slack;
  const blind = slack * AI_SKILL.LANE_BLINDNESS;

  let best: PassEval | null = null;
  /** El mejor pase DIRECTO (ni pared ni hueco), para no preferir la pared si lo hay. */
  let bestDirect: PassEval | null = null;

  for (const mate of mates) {
    if (mate.role === "gk") continue;
    if (!seesMate(player, mate, slack)) continue;

    // Rivales CON portero: la linea la traza el penultimo adversario, y el
    // portero es uno. Sin el salia un defensa por delante de donde debe estar.
    if (
      isInOffsidePosition(
        mate.pos.x,
        player.side,
        ball.pos.x,
        opponents,
        0,
        mates.filter((m) => m.role !== "gk"),
      )
    ) {
      continue;
    }

    const mateAdvance = dist(mate.pos, goal);
    const advance = myAdvance - mateAdvance;
    const backward = advance < -8;
    const forward = (mate.pos.x - player.pos.x) * dir;
    const openness = nearestOpponentDist(mate.pos, opponents);
    const free = openness >= AI.FREE_RADIUS;
    const passDistApprox = dist(player.pos, mate.pos);
    // Combinacion corta: excepcion tiki-taka al veto de pases atras en arcade.
    const shortCombo = passDistApprox <= AI.SHORT_PASS_MAX;

    if (backward && !profile.allowBackwardPass && !free && !shortCombo) continue;
    // Hacia atras, a un compañero LIBRE se puede jugar largo en campo grande
    // (`backPassReach`): con el tope de 55 de siempre, el pase al central que
    // espera solo detras —el pase atras de verdad— no existia.
    const maxBack = free ? profile.backPassReach : 55;
    if (forward < (profile.allowBackwardPass || free || shortCombo ? -maxBack : 12)) continue;

    // Libre / corto: se permite pase lateral aunque no gane mucha profundidad.
    const minAdv = free || shortCombo
      ? -35
      : profile.allowBackwardPass
        ? AI.PASS_MIN_ADVANTAGE * 0.55
        : AI.PASS_MIN_ADVANTAGE * 0.85;
    if (
      !backward &&
      !free &&
      !shortCombo &&
      advance < minAdv &&
      Math.abs(mate.pos.y - player.pos.y) < 90
    ) {
      continue;
    }

    if (profile.allowBackwardPass && !free && !shortCombo) {
      if (pressured && backward && mate.tacticRole !== "cb" && mate.tacticRole !== "fb") continue;
      if (!pressured && advance < AI.PASS_MIN_ADVANTAGE * 0.55 && !backward) continue;
      if (pressured && backward && advance < -AI.PASS_MIN_ADVANTAGE * 1.2) continue;
    }

    const leadTime =
      AI.PASS_LEAD_TIME *
      (shortCombo ? 0.55 : 1) *
      (mate.tacticRole === "st" ? 1.4 : mate.tacticRole === "wm" ? 1.1 : 1);
    const lead = vec(
      mate.pos.x + mate.vel.x * leadTime,
      mate.pos.y + mate.vel.y * leadTime,
    );
    lead.x = clamp(lead.x, mate.radius, FIELD.WIDTH - mate.radius);
    lead.y = clamp(lead.y, mate.radius, FIELD.HEIGHT - mate.radius);

    const realClear = laneClearance(ball.pos, lead, opponents);
    // Lo que el pasador CREE que hay de hueco: el torpe mira al compañero, no
    // al rival que se mete en medio, y todos los carriles le parecen de un
    // hueco normal (`LANE_CLEARANCE` x2). `lastSafe` (reciclar) usa el de verdad.
    // (Sin rivales el carril mide infinito: se acota antes de mezclar.)
    const passClear =
      blind > 0
        ? Math.min(realClear, FIELD.WIDTH) * (1 - blind) + AI.LANE_CLEARANCE * 2 * blind
        : realClear;
    /*
     * Se probo despejar la potencia de la distancia (como en el pase al hueco,
     * con `passPowerForDistance`) para que los pases largos llegasen vivos, y
     * SALIO PEOR: el balon en el ultimo tercio del 11v11 cayo del 44% al 30%.
     * Un pase que llega con ritmo tambien es un pase que el companero no
     * controla, y acaba en rechace. Se queda la regla de siempre.
     */
    const passPower = clamp(passDistApprox / (shortCombo ? 520 : 680), shortCombo ? 0.32 : 0.38, backward ? 0.72 : 0.95);

    const boldLane = 1 / (1 + (boldness - 1) * AI.BOLDNESS_LANE_GAIN);
    /*
     * Holgura de carril exigida, con SUELO fisico.
     *
     * `AI.LANE_CLEARANCE` es un numero fijo (28) mientras el balon va de radio
     * 13 en 3v3 a 6 en 11v11 — el mismo error que ya se cometio con el
     * larguero y con el brazo del portero. Con los multiplicadores bajos
     * (presionado + combinacion corta) el umbral caia a ~8,8 unidades: menos
     * que el propio radio del balon, o sea que la IA metia pases por sitios
     * por donde el balon no cabe.
     *
     * Medido antes de esto: en 3v3 se interceptaba el 34% de los pases, contra
     * el 22% del 11v11. La diferencia no era tactica, era geometrica.
     */
    const ballR = ACTIVE_MATCH_MODE.physics.ballRadius;
    const clearNeed = Math.max(
      ballR + AI.LANE_MIN_MARGIN,
      AI.LANE_CLEARANCE * (pressured ? 0.45 : 0.55) * (free || shortCombo ? 0.7 : 1) * boldLane,
    );
    const directBlocked = passClear < clearNeed;

    const wideBonus =
      Math.abs(mate.pos.y - HALF_HEIGHT) > AI.SUPPORT_WIDTH * 0.45 ? AI.PASS_WIDE_BONUS : 1;
    const mateSide = Math.sign(mate.pos.y - HALF_HEIGHT);
    const switchBonus =
      ballSide !== 0 && mateSide !== 0 && ballSide !== mateSide ? AI.PASS_SWITCH_BONUS : 1;
    const roleBonus = passRoleBonus(mate.tacticRole, backward);
    const freeMul = opennessScore(openness);

    /*
     * Lo que vale este companero al margen de DONDE se le ponga el balon.
     *
     * Existe para poder puntuar los dos destinos —al pie y al hueco— con la
     * misma formula, cambiando solo lo que de verdad cambia: cuanto adelanta el
     * balon y como de limpio esta ese carril. Antes el pase al hueco traia su
     * propia puntuacion de `scoreThroughPass` y se comparaba en crudo contra
     * esta, que es un producto de siete factores sin techo. Medido: el hueco
     * vivia en p50=18-35 y el pie en p50=492-1855. La comparacion la ganaba el
     * pie 25-50 veces de cada 1, pasara lo que pasara, y por eso el 0% de los
     * pases iban al hueco. Mismo fallo que ya se arreglo entre tiro y pase con
     * `normalizeActionScore`, un nivel mas abajo.
     */
    const mateBase = wideBonus * switchBonus * roleBonus * profile.passBias * 100;

    /** El pase al hueco a este companero, ya en la moneda del pase al pie. */
    const throughOption = (): PassEval | null => {
      if (craft <= 0) return null;
      const through = scoreThroughPass(player, mate, ball, opponents, dir, ball.pos.x);
      if (!through) return null;
      const throughClear = laneClearance(ball.pos, through.target, opponents);
      if (throughClear < ballR + AI.LANE_MIN_MARGIN) { return null; }
      return {
        // `advance + gain`: el balon acaba mas arriba de donde esta el
        // receptor ahora, y esa es justo la ventaja que se compra.
        score:
          ((advance + through.gain) / 120) *
          (throughClear / AI.LANE_CLEARANCE) *
          through.quality *
          mateBase *
          directnessMul(directness, dist(player.pos, through.target), forward) *
          (1 + counter * clamp(forward / TACTICS.COUNTER_FULL_FORWARD, 0, 1)) *
          craft,
        target: through.target,
        mate,
        power: through.power,
        backward,
        through: true,
      };
    };

    // Directo tapado: probar pared o pase al hueco.
    if (directBlocked) {
      let usedAlt = false;
      const through = throughOption();
      if (through && through.score > AI.MIN_PASS_SCORE * 0.55) {
        best = considerPass(best, through);
        usedAlt = true;
      }
      const wall = evaluateWallPassTo(player, ball, mate, lead, opponents, advance, backward, craft);
      if (wall) {
        wall.score *= trianglePassMul(player, mate, mates, opponents);
        best = considerPass(best, wall);
        usedAlt = true;
      }
      if (usedAlt) continue;
    }

    /*
     * Lo que abre un pase atras, EN LA MISMA MONEDA que lo que avanza uno
     * hacia delante.
     *
     * El primer intento fue un multiplicador sobre la puntuacion, y no podia
     * funcionar: el termino de avance mete un pase atras con 20/120 y uno
     * hacia delante de 200 unidades con 200/120 — diez veces mas. Ningun
     * multiplicador razonable cierra esa diferencia.
     *
     * Asi que la construccion se convierte a "unidades de avance
     * equivalentes": un receptor libre con dos salidas limpias vale tanto como
     * progresar ~160. Y uno presionado o sin salidas sigue valiendo poco, que
     * es lo que impide que la IA se dedique a tocar hacia atras.
     */
    const backwardEquiv =
      backward && profile.allowBackwardPass
        ? AI.BUILDUP_ADVANCE_EQUIV * (buildUpValue(mate, mates, opponents, dir) - 1)
        : 0;

    let score =
      (Math.max(advance, backwardEquiv, free || shortCombo ? 40 : 20) / 120) *
      (passClear / AI.LANE_CLEARANCE) *
      freeMul *
      mateBase *
      directnessMul(directness, passDistApprox, forward) *
      (1 + counter * clamp(forward / TACTICS.COUNTER_FULL_FORWARD, 0, 1));

    if (openness > AI.PASS_OPEN_BONUS_DIST) score *= 1.25;
    if (free) score *= 1.15;
    // A igualdad de condiciones, la pelota va para la persona: un companero
    // humano al que nunca le llega el balon es un jugador que no vuelve.
    if (mate.human) score *= AI.HUMAN_PASS_BONUS;
    if (shortCombo) {
      // Cuanto mas cerca del area rival, mas vale la combinacion corta.
      const progress = clamp(1 - myAdvance / FIELD.WIDTH, 0, 1);
      const zoneScale = AI.TIKI_OWN_HALF_SCALE + (1 - AI.TIKI_OWN_HALF_SCALE) * progress;
      score *= AI.TIKI_SHORT_BONUS * (0.85 + tempo * 0.35) * zoneScale;
    }
    score *= trianglePassMul(player, mate, mates, opponents);

    const offMul = offsideSpaceScore(
      lead.x,
      player.side,
      ball.pos.x,
      opponents,
      mates.filter((m) => m.role !== "gk"),
    );
    score *= offMul;
    if (offMul <= 0.02) continue;

    // Tercer hombre: circular, no devolver al que te la dio.
    if (team?.lastPasserId === mate.id) {
      score *= pressured ? 0.85 : AI.RETURN_PASS_PENALTY;
    } else if (team?.lastPasserId && team.lastPasserId !== mate.id && !backward) {
      score *= AI.THIRD_MAN_BONUS;
    }

    if (backward) {
      score *=
        shortCombo || profile.allowBackwardPass
          ? AI.PASS_SAFETY_BONUS * (pressured ? 1 : 0.75)
          : 0.25;
    }
    if (pressured && backward && (mate.tacticRole === "fb" || mate.tacticRole === "cb")) {
      score *= profile.allowBackwardPass || shortCombo ? AI.PASS_SAFETY_BONUS : 0.15;
    }
    if (team?.phase === "attack" && mate.tacticRole === "st" && forward > 80) score *= 1.2;
    if (!profile.allowBackwardPass && !free && !shortCombo && advance < 40) score *= 0.55;

    const direct: PassEval = {
      score,
      target: lead,
      mate,
      power: passPower,
      backward,
      through: false,
    };
    best = considerPass(best, direct);
    bestDirect = considerPass(bestDirect, direct);
    // Los pases atras directos tambien son construccion: hasta ahora solo se
    // apuntaban los de pared, y la rama `construccion:atras` casi no veia
    // ninguno.
    if (backward) lastBackward = considerPass(lastBackward, direct);
    if (AI.RECYCLE_MIN_LANE > 0 && realClear >= AI.RECYCLE_MIN_LANE && openness >= AI.RECYCLE_MIN_OPEN) {
      // Entre los seguros, el que menos terreno pierde: al lado antes que atras.
      // Sin esto el central libre y lejos ganaba siempre, y el 30-42 % de los
      // pases acababan yendo hacia atras.
      const ground = 1 + clamp(forward, -AI.RECYCLE_GROUND, AI.RECYCLE_GROUND) / (2 * AI.RECYCLE_GROUND);
      const safety =
        Math.min(realClear, AI.RECYCLE_MIN_LANE * 2) * Math.min(openness, AI.FREE_RADIUS * 1.5) * ground;
      if (!lastSafe || safety > lastSafe.safety) lastSafe = { pass: direct, safety };
    }

    /*
     * Pase al hueco.
     *
     * Ya se comparan dos numeros de la misma escala, asi que el umbral vuelve a
     * significar lo que dice: se pone al hueco cuando al hueco se gana MAS que
     * al pie. El margen de 0,95 es el respeto que se le tiene al pase seguro —
     * a igualdad, se juega al pie.
     *
     * La puerta ya NO excluye la combinacion corta, y ese era el ultimo cerrojo.
     * Excluirla tenia sentido para un juego donde se puede pegar un balonazo de
     * 600 unidades; aqui el balon raso alcanza 440 utiles en 11v11, asi que el
     * unico hueco que existe fisicamente es el del companero cercano — es decir,
     * justo el que la condicion `!shortCombo` estaba tirando. Medido: 1147 de
     * 1217 intentos morian por alcance antes de tocar nada.
     */
    if (!backward && forward > AI.THROUGH_MIN_SPACE) {
      const through = throughOption();
      if (through && through.score > score * 0.95) { best = considerPass(best, through); }
    }

    /*
     * La pared con el pase directo LIMPIO, solo si abre mucho mas
     * (`AI.WALL_OVER_DIRECT`). Antes bastaba con que puntuara el 95 % del
     * directo, y con el plus de pared la ganaba a menudo: el 20-33 % de los
     * pases de 3v3 a 7v7 iban a la banda, y en el 55-68 % de esos habia un
     * compañero libre por el centro con el pase directo limpio. Guardiola: si
     * el pase directo esta, se da el directo; la pared es para cuando no.
     */
    const wall = evaluateWallPassTo(player, ball, mate, lead, opponents, advance, backward, craft);
    if (wall && wall.score > score * AI.WALL_OVER_DIRECT) best = considerPass(best, wall);
  }

  if (!lastSafe) lastSafe = keeperRecycle(player, ball, mates, opponents);

  /*
   * Y si la pared gana a un compañero TAPADO habiendo un pase directo decente
   * a otro (`AI.WALL_OVER_ANY_DIRECT`), se da el directo: con la regla de
   * arriba sola, en 7v7 el 67 % de las paredes que quedaban tenian a alguien
   * libre por el centro.
   */
  if (best?.wall && bestDirect && bestDirect.score * AI.WALL_OVER_ANY_DIRECT >= best.score) best = bestDirect;

  // Todo lo de arriba se compara en CRUDO entre si — la eleccion del mejor pase
  // no cambia. Solo el valor que sale hacia `chooseAttack` va normalizado, que
  // es donde tiene que competir contra el tiro.
  if (!best) return null;
  return { ...best, score: normalizeActionScore(best.score) };
}

/**
 * Reciclar al PORTERO (`AI.RECYCLE_GK_*`): solo en campo propio, con el carril
 * muy limpio y el portero sin nadie cerca. Rodado y flojo, al pie: es un pase
 * de seguridad, no un envio.
 */
function keeperRecycle(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): { pass: PassEval; safety: number } | null {
  if (AI.RECYCLE_GK_ROOM <= 0 || AI.RECYCLE_MIN_LANE <= 0) return null;
  const keeper = mates.find((m) => m.role === "gk");
  if (!keeper) return null;
  const advance = player.side === "home" ? player.pos.x / FIELD.WIDTH : 1 - player.pos.x / FIELD.WIDTH;
  if (advance > AI.RECYCLE_GK_MAX_ADVANCE) return null;
  if (nearestOpponentDist(keeper.pos, opponents) < AI.RECYCLE_GK_ROOM) return null;
  // Solo cuentan los rivales por DELANTE en la direccion del pase: el que
  // aprieta, pegado al balon y por detras, no alcanza un balon que se aleja
  // de el, y con `laneClearance` a secas anulaba esta salida casi siempre.
  const lane = laneClearanceAhead(ball.pos, keeper.pos, opponents);
  if (lane < AI.RECYCLE_MIN_LANE * 2) return null;
  const reach = dist(ball.pos, keeper.pos);
  return {
    pass: {
      score: 0,
      target: vec(keeper.pos.x, keeper.pos.y),
      mate: keeper,
      power: clamp(reach / 680, 0.3, 0.6),
      backward: true,
      through: false,
    },
    safety: 0,
  };
}

/** ¿Hay un tercero que cierre el triangulo de pase? */
function trianglePassMul(
  player: Player,
  mate: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
): number {
  const ab = dist(player.pos, mate.pos);
  if (ab > AI.SHORT_PASS_MAX * 1.15) return 1;

  for (const third of mates) {
    if (third.id === mate.id || third.id === player.id || third.role === "gk") continue;
    const bc = dist(mate.pos, third.pos);
    const ac = dist(player.pos, third.pos);
    if (bc > AI.SHORT_PASS_MAX * 1.25 || ac < 36) continue;

    // Angulo en el portador: ni colineal ni demasiado cerrado.
    const ang = angleAt(player.pos, mate.pos, third.pos);
    if (ang < 0.4 || ang > 2.5) continue;

    const thirdOpen = nearestOpponentDist(third.pos, opponents);
    if (thirdOpen < AI.MARKED_RADIUS * 0.65) continue;
    return AI.TRIANGLE_PASS_BONUS;
  }
  return 1;
}

function angleAt(vertex: Vec2, a: Vec2, b: Vec2): number {
  const ax = a.x - vertex.x;
  const ay = a.y - vertex.y;
  const bx = b.x - vertex.x;
  const by = b.y - vertex.y;
  const la = len2(ax, ay) || 1;
  const lb = len2(bx, by) || 1;
  return dacos(clamp((ax * bx + ay * by) / (la * lb), -1, 1));
}

/**
 * Pase a pared (banda superior/inferior). Apunta al reflejo del companero
 * para que el rebote le caiga al pie — clasico de Haxball/futbol sala.
 */
function evaluateWallPassTo(
  _player: Player,
  ball: Ball,
  mate: Player,
  lead: Vec2,
  opponents: readonly Player[],
  advance: number,
  backward: boolean,
  /** 1 - `decisionSlack`: la pared es una lectura de jugador hecho. */
  craft = 1,
): PassEval | null {
  if (craft <= 0) return null;
  const walls = [0, FIELD.HEIGHT] as const;
  let best: PassEval | null = null;

  for (const wallY of walls) {
    const reflected = vec(lead.x, 2 * wallY - lead.y);
    const hit = segmentWallHit(ball.pos, reflected, wallY);
    if (!hit) continue;

    // El rebote tiene que caer cerca del receptor, no al otro lado del campo.
    if (dist(hit, lead) > 520) continue;
    if (dist(ball.pos, hit) < 40) continue;

    const clearIn = laneClearance(ball.pos, hit, opponents);
    const clearOut = laneClearance(hit, lead, opponents);
    if (clearIn < AI.LANE_CLEARANCE * 0.55) continue;
    if (clearOut < AI.LANE_CLEARANCE * 0.4) continue;

    const openness = nearestOpponentDist(mate.pos, opponents);
    const freeMul = opennessScore(openness);
    const pathLen = dist(ball.pos, hit) + dist(hit, lead) / Math.max(0.55, BALL_WALL_RESTITUTION);

    let score =
      (Math.max(advance, 25) / 120) *
      ((clearIn + clearOut) / (AI.LANE_CLEARANCE * 2)) *
      freeMul *
      AI.PASS_WALL_BONUS *
      ACTIVE_MATCH_MODE.ai.passBias *
      100;

    // La pared vale especialmente si el carril directo esta sucio.
    const directClear = laneClearance(ball.pos, lead, opponents);
    if (directClear < AI.LANE_CLEARANCE * 0.7) score *= 1.35;
    if (openness >= AI.FREE_RADIUS) score *= 1.2;
    if (backward && !ACTIVE_MATCH_MODE.ai.allowBackwardPass) score *= 0.3;
    if (craft < 1) score *= craft;

    const candidate: PassEval = {
      score,
      // Se apunta al punto de la pared; el rebote hace el resto.
      target: hit,
      mate,
      power: clamp(pathLen / 720, 0.42, 0.98),
      backward,
      wall: true,
    };
    best = considerPass(best, candidate);
    if (candidate.backward) lastBackward = considerPass(lastBackward, candidate);
  }

  return best;
}

/** Interseccion del segmento a->b con la banda horizontal y = wallY. */
function segmentWallHit(a: Vec2, b: Vec2, wallY: number): Vec2 | null {
  const dy = b.y - a.y;
  if (Math.abs(dy) < 1e-6) return null;
  const t = (wallY - a.y) / dy;
  if (t <= 0.05 || t >= 0.98) return null;
  const x = a.x + (b.x - a.x) * t;
  if (x < 40 || x > FIELD.WIDTH - 40) return null;
  return vec(x, wallY);
}

// Restitucion de pared del balon (para estimar potencia del pase a banda).
const BALL_WALL_RESTITUTION = 0.72;

function passRoleBonus(role: TacticalRole, backward: boolean): number {
  if (backward) {
    if (role === "fb" || role === "cb") return 1.1;
    if (role === "cm") return 0.9;
    return 0.55;
  }
  if (role === "wm" || role === "fb") return 1.12;
  if (role === "st") return 1.25;
  return 1;
}

/**
 * Centro desde banda. El timing es la clave: el receptor tiene que llegar al
 * punto de caida cuando el balon, no antes (offsides mental) ni mucho despues.
 */
export function evaluateCross(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): CrossEval | null {
  if (!isInCrossingZone(player)) return null;

  const dir = attackDir(player.side);
  const goal = attackedGoal(player.side);
  let best: CrossEval | null = null;

  for (const mate of mates) {
    if (mate.role === "gk") continue;
    // Centros a gente que ataca el area: ST, CM llegando, WM del otro lado.
    if (mate.tacticRole !== "st" && mate.tacticRole !== "cm" && mate.tacticRole !== "wm") {
      continue;
    }

    const base = defaultCrossTarget(player.side, player.pos.y);
    // Ajusta la caida hacia donde el companero ya se dirige.
    const boxNear = player.side === "home" ? FIELD.WIDTH - FIELD.BOX_WIDTH - 40 : 40;
    const boxFar = player.side === "home" ? FIELD.WIDTH - 40 : FIELD.BOX_WIDTH + 40;
    const landing = vec(
      clamp(mate.pos.x + mate.vel.x * 0.45, Math.min(boxNear, boxFar), Math.max(boxNear, boxFar)),
      clamp(
        0.55 * base.y + 0.45 * (mate.pos.y + mate.vel.y * 0.35),
        FIELD.HEIGHT / 2 - KICK.CROSS_BOX_HALF_WIDTH,
        FIELD.HEIGHT / 2 + KICK.CROSS_BOX_HALF_WIDTH,
      ),
    );

    // El companero tiene que estar atacando el area, no plantado en su campo.
    const mateToGoal = Math.abs(goal.x - mate.pos.x);
    if (mateToGoal > FIELD.WIDTH * 0.48) continue;
    if ((mate.pos.x - player.pos.x) * dir < -80) continue;

    const flightDist = dist(ball.pos, landing);
    const flightTime = flightDist / (KICK.CROSS_SPEED * 0.78);
    const mateDist = dist(mate.pos, landing);
    const mateSpeed = Math.max(len2(mate.vel.x, mate.vel.y), 180);
    const arriveTime = mateDist / mateSpeed;
    const timingError = Math.abs(arriveTime - flightTime);

    // Fuera de ventana: el centro cae vacio o al pie del defensa.
    if (timingError > AI.CROSS_TIMING_WINDOW * 1.8) continue;

    const clear = laneClearance(ball.pos, landing, opponents);
    if (clear < AI.LANE_CLEARANCE * 0.35) continue;

    const openness = nearestOpponentDist(landing, opponents);
    const freeMul = opennessScore(openness);
    const timingMul =
      1.35 - clamp(timingError / AI.CROSS_TIMING_WINDOW, 0, 1.2) * 0.85;
    const roleMul = mate.tacticRole === "st" ? 1.3 : mate.tacticRole === "cm" ? 1.05 : 1.12;

    // xG: angulos cerrados y centros rasos al primer poste marcan demasiado en FM-like sims.
    const angleFromByline = datan2(
      Math.abs(landing.y - HALF_HEIGHT),
      Math.max(Math.abs(landing.x - goal.x), 40),
    );
    let xgMul = 1;
    if (angleFromByline < AI.CROSS_MIN_ANGLE) {
      xgMul *= AI.CROSS_TIGHT_ANGLE_XG + (angleFromByline / AI.CROSS_MIN_ANGLE) * 0.35;
    }
    const nearPost =
      Math.abs(landing.y - HALF_HEIGHT) < goalMouth().half * 0.38 && flightDist < 220;
    if (nearPost && angleFromByline < AI.CROSS_MIN_ANGLE * 1.15) {
      xgMul *= AI.CROSS_NEAR_POST_XG;
    }

    let score =
      (clear / AI.LANE_CLEARANCE) *
      freeMul *
      timingMul *
      roleMul *
      xgMul *
      AI.CROSS_BONUS *
      ACTIVE_MATCH_MODE.ai.passBias *
      100;

    // Segundo palo desmarcado: el centro clasico.
    const farSide =
      Math.sign(landing.y - HALF_HEIGHT) !== Math.sign(player.pos.y - HALF_HEIGHT);
    if (farSide) score *= 1.2;
    if (openness >= AI.FREE_RADIUS) score *= 1.15;
    if (mate.tacticRole === "st" && mateToGoal < FIELD.BOX_WIDTH * 1.1) score *= 1.18;

    const candidate: CrossEval = {
      score,
      target: landing,
      mate,
      power: clamp(flightDist / 780, 0.55, 0.98),
      timingError,
    };
    if (!best || candidate.score > best.score) best = candidate;
  }

  // Misma normalizacion que el pase: al ser monotona, cual centro se elige no
  // cambia, y pasa a competir con el tiro en la escala correcta.
  if (!best) return null;
  return { ...best, score: normalizeActionScore(best.score) };
}

/** Dispersion angular del pase segun distancia, presion y habilidad. */
function aiPassSpread(
  player: Player,
  target: Vec2,
  kind: "pass" | "cross",
  pressured: boolean,
  mul = 1,
): number {
  const passDist = dist(player.pos, target);
  const err = passAngularError(player, passDist, pressured, kind);
  return Math.max(AI.PASS_SPREAD * 0.45, err) * mul;
}

/** Elige la mejor opcion de ataque segun modo (arcade vs 11v11). */
/**
 * Rama de `chooseAttack` que decidio la jugada. El orden de la lista es el
 * orden en que se evaluan: la primera que se cumple gana, asi que una rama
 * temprana muy permisiva deja muertas a todas las de abajo.
 */
export type AttackBranch =
  | "sin-un-toque:conduce"
  | "un-toque:pase-corto"
  | "sin-remate-listo:conduce"
  | "finta"
  | "remate:zona-de-gol"
  | "remate:en-rango"
  | "tiki-taka:pase-corto"
  | "centro:timing"
  | "centro:primeras"
  | "companero-libre:pase"
  | "remate:forzado-arcade"
  | "remate:presionado"
  | "centro:tardio"
  | "pase:gana-al-tiro"
  | "remate:umbral"
  | "construccion:atras"
  | "pase:relajado"
  | "pase:reciclaje-atras"
  | "tiki-taka:ultimo-filtro"
  | "remate:acorralado"
  | "aguanta:de-espaldas"
  | "desborde:banda"
  | "centro:desde-el-fondo"
  | "centro:antes-que-reciclar"
  | "pase:recicla"
  | "conduce";

/** Una decision de ataque con los numeros que la produjeron. */
export interface AttackTrace {
  branch: AttackBranch;
  kind: AttackChoice["kind"];
  playerId: string;
  side: TeamSide;
  role: TacticalRole;
  /** Rol del companero elegido, si la jugada fue un pase o centro. */
  receiverRole: TacticalRole | null;
  shotScore: number;
  passScore: number | null;
  crossScore: number | null;
  /** Distancia a la linea de gol rival, para separar campo propio de area. */
  toGoalLine: number;
  pressured: boolean;
  /**
   * La jugada fue un pase AL HUECO, no al pie.
   *
   * No es una rama propia: el pase al hueco se elige dentro de `evaluatePass`
   * y sale por la misma puerta que cualquier otro pase, asi que sin esto la
   * tabla de ramas no distingue "se la doy al pie" de "se la pongo a la
   * carrera" — y son dos futbolistas distintos.
   */
  through: boolean;
  /** La jugada fue un pase a la pared (a la banda, para que rebote). */
  wall: boolean;
}

/**
 * Registrador opt-in de decisiones. Apagado por defecto y con coste cero
 * (una comparacion con null), para que el auditor pueda ver QUE rama gana sin
 * que el juego pague nada. No toca el estado de la simulacion: solo lee.
 */
let tracer: ((trace: AttackTrace) => void) | null = null;

export function setAttackTracer(fn: ((trace: AttackTrace) => void) | null): void {
  tracer = fn;
}

export function chooseAttack(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
  holdTime: number,
  pressureDist: number,
  mentality: { passBias: number; shootBias: number; dribbleBias: number; boldness?: number } = {
    passBias: 1,
    shootBias: 1,
    dribbleBias: 1,
  },
  team?: TeamTacticsState,
  roll: number = 0.5,
): AttackChoice {
  const boldness = mentality.boldness ?? 1;
  const profile = ACTIVE_MATCH_MODE.ai;
  const goal = attackedGoal(player.side);
  const toGoalLine = Math.abs(goal.x - player.pos.x);
  const dir = attackDir(player.side);
  const range = shootRange();
  const close = closeShotRange();
  const standoff = AI.SHOOT_STANDOFF * profile.standoffScale;
  const tempo = team?.instructions.tempo ?? 0.5;
  const oneTouchReady = holdTime >= AI.ONE_TOUCH_HOLD;
  const shotReady = holdTime >= profile.minHold;
  const pressured = pressureDist < AI.PRESSURE_RADIUS;

  // Se rellenan segun se van calculando; el trazador las lee al salir. Son
  // `let` porque hay ramas que salen antes de que existan pase o tiro.
  let seenShot = 0;
  let seenPass: PassEval | null = null;
  let seenCross: CrossEval | null = null;

  /** Marca la rama por la que sale la decision y la devuelve intacta. */
  const emit = (
    branch: AttackBranch,
    choice: AttackChoice,
    receiver: Player | null = null,
  ): AttackChoice => {
    if (tracer) {
      tracer({
        branch,
        kind: choice.kind,
        playerId: player.id,
        side: player.side,
        role: player.tacticRole,
        receiverRole: receiver?.tacticRole ?? null,
        // Solo si el pase que se juega ES el que evaluo `evaluatePass`: la
        // rama de construccion juega otro (`lastBuildUpPass`), que nunca es al
        // hueco porque va hacia atras.
        through:
          choice.kind === "pass" &&
          (seenPass?.through ?? false) &&
          receiver?.id === seenPass?.mate.id,
        // Se apunta a la banda misma (`evaluateWallPassTo`): la geometria basta.
        wall: choice.kind === "pass" && (choice.target.y === 0 || choice.target.y === FIELD.HEIGHT),
        shotScore: seenShot,
        passScore: seenPass?.score ?? null,
        crossScore: seenCross?.score ?? null,
        toGoalLine,
        pressured,
      });
    }
    return choice;
  };


  // Cubrirse es un gesto de jugador hecho: escala con el nivel, como el cuerpo.
  const shield =
    roll < bodyScale(player) ? shieldTarget(player, mates, opponents, holdTime, toGoalLine < close) : null;
  // Desborde por banda hacia el fondo en vez de reciclar (`AI.DRIVE_*`). Se
  // arranca con `DRIVE_CHANCE`; el que ya va lanzado la termina.
  const driveSkill = AI.DRIVE_CHANCE * (1 - decisionSlack(player));
  const driveAim = driveSkill > 0 ? wingDriveTarget(player, opponents) : null;
  const alreadyDriving = driveAim !== null && headingTo(player, driveAim);
  const drive = driveAim && (alreadyDriving || roll < driveSkill) ? driveAim : null;
  const driveOn = (): AttackChoice | null =>
    drive ? emit("desborde:banda", { kind: "dribble", target: drive, power: 0, spread: 0 }) : null;

  if (!oneTouchReady) {
    if (shield) return emit("aguanta:de-espaldas", { kind: "dribble", target: shield, power: 0, spread: 0 });
    const safe = findOpenSpace(player, ball, mates, opponents, goal, dir, standoff);
    return emit("sin-un-toque:conduce", { kind: "dribble", target: safe, power: 0, spread: 0 });
  }

  const pass = evaluatePass(player, ball, mates, opponents, team, boldness);
  seenPass = pass;
  /*
   * RECICLAR (`AI.RECYCLE_*`): apretado, antes de conducir contra el rival o de
   * aguantar de espaldas, el pase seguro si lo hay — atras o al lado tambien.
   * Solo se mira donde de otro modo se conduciria: los pases que progresan y
   * el tiro se deciden antes.
   */
  const safePass =
    pressureDist < AI.RECYCLE_PRESSURE && roll < readScale(player) ? lastRecyclePass() : null;
  const recycle = (): AttackChoice | null =>
    safePass
      ? emit(
          "pase:recicla",
          {
            kind: "pass",
            target: safePass.target,
            power: safePass.power,
            spread: aiPassSpread(player, safePass.target, "pass", pressured, 1),
          },
          safePass.mate,
        )
      : null;
  /*
   * El centro se evalua ANTES de la rama del pase corto, pero solo si el
   * jugador esta en la banda: fuera de ahi `evaluateCross` devuelve null en la
   * primera linea y no tiene sentido pagar el recorrido de companeros.
   */
  const crossFromWing = isInCrossingZone(player)
    ? evaluateCross(player, ball, mates, opponents)
    : null;
  if (crossFromWing) seenCross = crossFromWing;
  const shortPass =
    pass && dist(player.pos, pass.target) <= AI.SHORT_PASS_MAX ? pass : null;
  /*
   * CENTRO DESDE EL FONDO (`AI.CROSS_BYLINE_DEPTH`): llegado al fondo, con
   * alguien dentro del area o entrando (`arrivingAt`), se centra con el liston
   * normal. El triple de `centro:primeras` es para el centro temprano, que
   * cortaba la jugada antes de que subiera el bloque; desde el fondo el bloque
   * ya esta arriba.
   */
  // Hay a quien centrar (dentro del area o entrando) y el centro pasa el liston normal.
  const crossOn =
    crossFromWing !== null &&
    (inAttackingBox(crossFromWing.mate) || pointInAttackingBox(arrivingAt(crossFromWing.mate), player.side)) &&
    crossFromWing.score >= AI.MIN_CROSS_SCORE_NORM * profile.passBias;
  // Desde el fondo se centra aunque el centro no sea limpio: a la zona del
  // compañero mas libre del area (`hopefulCross`). Casi todos los centros de
  // verdad son asi, y la mayoria no llegan.
  const bylineCross =
    toGoalLine < AI.CROSS_BYLINE_DEPTH && isInCrossingZone(player)
      ? crossOn
        ? crossFromWing
        : hopefulCross(player, ball, mates, opponents)
      : null;
  /** En la zona, con centro de verdad, antes que reciclar o conducir (`AI.CROSS_WIDE_CHANCE`). */
  const wideCross = crossOn && roll < AI.CROSS_WIDE_CHANCE ? crossFromWing : null;
  const crossBeforeRecycle = (): AttackChoice | null =>
    wideCross
      ? emit(
          "centro:antes-que-reciclar",
          {
            kind: "cross",
            target: wideCross.target,
            power: wideCross.power,
            spread: aiPassSpread(player, wideCross.target, "cross", pressured, 0.7 + wideCross.timingError),
          },
          wideCross.mate,
        )
      : null;
  const crossFromByline = (): AttackChoice | null =>
    bylineCross
      ? emit(
          "centro:desde-el-fondo",
          {
            kind: "cross",
            target: bylineCross.target,
            power: bylineCross.power,
            spread: aiPassSpread(player, bylineCross.target, "cross", pressured, bylineCross.score > 0 ? 0.7 : 1),
          },
          bylineCross.mate,
        )
      : null;


  // Un toque: soltar en cuanto hay apoyo corto, sin esperar a asentar el remate.
  if (!shotReady) {
    if (
      shortPass &&
      shortPass.score > AI.MIN_PASS_SCORE_NORM * 0.55 * profile.passBias &&
      (pressured ||
        pressureDist < AI.ONE_TOUCH_PRESSURE ||
        shortPass.score > AI.MIN_PASS_SCORE_NORM * (0.9 - tempo * 0.2))
    ) {
      return emit(
        "un-toque:pase-corto",
        {
          kind: "pass",
          target: shortPass.target,
          power: Math.min(shortPass.power, 0.55),
          spread: aiPassSpread(player, shortPass.target, "pass", pressured, 0.55),
        },
        shortPass.mate,
      );
    }
    /*
     * DESDE LA BANDA SE CENTRA, aunque el golpeo no este asentado.
     *
     * Esta rama solo miraba el pase corto y, si no lo habia, conducia. Un
     * centro no es un pase corto, asi que el que llegaba a la banda con el
     * balon nunca centraba: seguia regateando hasta perderlo o hasta salir de
     * la zona. Medido: el portador esta en zona de centro el 7-11% de los
     * ticks y desde ahi solo golpeaba 8-11 veces en 450 s de juego.
     *
     * Es ademas lo que pide el gesto: un centro se pega de primeras, sin
     * pararse a asentar el balon — pararse es justo lo que da tiempo al lateral
     * a llegar.
     */
    if (
      isInCrossingZone(player) &&
      crossFromWing &&
      /*
       * Y que el centro sea BUENO, no solo legal.
       *
       * `MIN_CROSS_SCORE_NORM` es 1,3 sobre una escala cuya mediana es ~15: lo
       * pasa casi cualquier centro. Con ese listón el extremo centraba en
       * cuanto pisaba la banda, y eso corta la jugada antes de que suba el
       * bloque — el test del bloque lo cazó: 3,13 jugadores en campo rival
       * contra los 3,5 que exige. Un centro que se pierde el 48-74% de las
       * veces no puede ser la primera opción, solo una buena opción.
       */
      crossFromWing.score >= AI.MIN_CROSS_SCORE_NORM * 3 * profile.passBias &&
      // Y si hay un pase corto mejor, se juega el pase: el centro es para
      // cuando no hay con quien combinar, no para cuando hay prisa.
      (!shortPass || crossFromWing.score > shortPass.score) &&
      /*
       * Y sobre todo: que haya alguien DENTRO DEL AREA a quien centrar.
       *
       * `evaluateCross` se conforma con que el companero "ataque el area"
       * (menos del 48% del campo hasta la porteria), que es media cancha. Con
       * ese criterio el extremo centraba a alguien que todavia venia corriendo,
       * el centro se perdia y la jugada moria antes de que subiera el bloque —
       * medido con el test del bloque: 3,24 jugadores en campo rival contra los
       * 3,5 que exige, y volvia a 3,5 al desactivar esta rama.
       *
       * Exigir que YA este dentro es lo que separa "centrar porque estoy en la
       * banda" de "centrar porque hay a quien". Es tambien lo que se pidio: que
       * centre CUANDO TIENE QUE centrar.
       */
      inAttackingBox(crossFromWing.mate)
    ) {
      return emit(
        "centro:primeras",
        {
          kind: "cross",
          target: crossFromWing.target,
          power: crossFromWing.power,
          spread: aiPassSpread(player, crossFromWing.target, "cross", pressured, 0.7),
        },
        crossFromWing.mate,
      );
    }

    const fromByline = crossFromByline();
    if (fromByline) return fromByline;
    const wide = crossBeforeRecycle();
    if (wide) return wide;
    const driving = driveOn();
    if (driving) return driving;
    const recycled = recycle();
    if (recycled) return recycled;
    if (shield) return emit("aguanta:de-espaldas", { kind: "dribble", target: shield, power: 0, spread: 0 });
    const safe = findOpenSpace(player, ball, mates, opponents, goal, dir, standoff);
    return emit("sin-remate-listo:conduce", { kind: "dribble", target: safe, power: 0, spread: 0 });
  }

  // Fake bajo presion: engaña y cambia el ritmo antes de decidir el pase/tiro.
  // Con tempo alto (tiki-taka) se finta menos: se prefiere circular.
  if (
    pressureDist < AI.FAKE_PRESSURE &&
    player.fakeCooldown <= 0 &&
    holdTime > 0.22 &&
    roll < AI.FAKE_CHANCE * (1.15 - tempo) &&
    !(shortPass && shortPass.score > AI.MIN_PASS_SCORE_NORM)
  ) {
    return emit("finta", {
      kind: "fake",
      target: findOpenSpace(player, ball, mates, opponents, goal, dir, standoff),
      power: 0,
      spread: 0,
    });
  }

  const shot =
    toGoalLine < range
      ? evaluateShot(player, ball, opponents, pressureDist)
      : { score: 0, target: goal, clearance: 0, power: 0.7, style: "power" as const };

  seenShot = shot.score;
  const cross = crossFromWing ?? evaluateCross(player, ball, mates, opponents);
  seenCross = cross;
  const inAttackHalf = toGoalLine < FIELD.WIDTH * 0.55;

  // Prioridad de remate en zona de gol: evita circulacion sin fin en semillas cerradas.
  if (oneTouchReady && toGoalLine < close * 1.05 && shot.score > 18) {
    return emit("remate:zona-de-gol", finishShot(player, shot, range));
  }
  // Desde la banda, cerca del fondo, se centra antes que chutar sin angulo.
  const fromByline = crossFromByline();
  if (fromByline) return fromByline;
  if (oneTouchReady && toGoalLine < range * 0.9 && shot.score > 14) {
    return emit("remate:en-rango", finishShot(player, shot, range));
  }
  // El desborde no se abandona por un pase a medias: sigue hasta el fondo o
  // hasta que haya a quien centrar (o un tiro, que se decide arriba).
  if (alreadyDriving && !crossOn) {
    const driving = driveOn();
    if (driving) return driving;
  }

  // El apetito de tiro del rol baja el umbral, no lo sube: un delantero (1)
  // decide igual que antes y los demas dejan de devolverla siempre.
  const roleShoot = playerRoleProfile(player).shootBias;
  const minShotEarly =
    ((toGoalLine < close ? AI.MIN_CLOSE_SHOT_SCORE : AI.MIN_SHOT_SCORE) *
      profile.shotThresholdScale) /
    (mentality.shootBias * profile.shootBias * Math.max(roleShoot, 0.35));

  // Tiki-taka: circulacion corta antes que conducir o forzar el tiro lejano.
  if (
    shortPass &&
    shortPass.score > AI.MIN_PASS_SCORE_NORM * 0.7 * profile.passBias &&
    shortPass.score > shot.score * AI.CIRCULATE_DRIBBLE_BIAS * (1.1 - tempo * 0.25) &&
    !(toGoalLine < close * 1.05 && shot.score >= minShotEarly * 0.72)
  ) {
    return emit(
      "tiki-taka:pase-corto",
      {
        kind: "pass",
        target: shortPass.target,
        power: shortPass.power,
        spread: aiPassSpread(player, shortPass.target, "pass", pressured, 0.6),
      },
      shortPass.mate,
    );
  }

  const freePass =
    pass &&
    !pass.backward &&
    nearestOpponentDist(pass.mate.pos, opponents) >= AI.FREE_RADIUS &&
    pass.score > AI.MIN_PASS_SCORE_NORM * 0.65;

  // Centro timed: prioriza sobre pase lateral y a menudo sobre tiro lejano.
  if (
    cross &&
    cross.score >= AI.MIN_CROSS_SCORE_NORM * profile.passBias &&
    cross.score > shot.score * 0.85 &&
    (!pass || cross.score > pass.score * 0.9)
  ) {
    return emit(
      "centro:timing",
      {
        kind: "cross",
        target: cross.target,
        power: cross.power,
        spread: aiPassSpread(player, cross.target, "cross", pressured, 0.55 + cross.timingError),
      },
      cross.mate,
    );
  }

  // Companero libre: casi siempre se la cede. Es futbol, no monologo.
  if (freePass && pass && pass.score > shot.score * 0.55) {
    return emit(
      "companero-libre:pase",
      {
        kind: "pass",
        target: pass.target,
        power: pass.power,
        spread: aiPassSpread(player, pass.target, "pass", pressured, 0.7),
      },
      pass.mate,
    );
  }

  // Arcade / sala: si estas en rango con vision decente, chuta. No regales el balon atras.
  const forcedShot =
    !profile.allowBackwardPass &&
    !freePass &&
    toGoalLine < range * 0.92 &&
    shot.score > 28 &&
    (toGoalLine < close || shot.clearance > AI.LANE_CLEARANCE * 0.3 || pressured);

  if (forcedShot) {
    return emit("remate:forzado-arcade", finishShot(player, shot, range));
  }

  const passBias =
    (pressured && profile.allowBackwardPass ? AI.PASS_PRESSURE_BIAS : AI.PASS_SHOOT_BIAS) *
    mentality.passBias *
    profile.passBias;

  // Bajo presion en campo rival: chutar > pasar atras.
  if (pressured && inAttackHalf && shot.score > 32 * profile.shotThresholdScale) {
    if (
      (!pass || pass.backward || pass.score < shot.score * 1.25) &&
      (!cross || cross.score < shot.score * 1.1)
    ) {
      return emit("remate:presionado", finishShot(player, shot, range));
    }
  }

  if (
    cross &&
    cross.score > AI.MIN_CROSS_SCORE_NORM * 0.85 * profile.passBias &&
    cross.score > shot.score * passBias
  ) {
    return emit(
      "centro:tardio",
      {
        kind: "cross",
        target: cross.target,
        power: cross.power,
        spread: aiPassSpread(player, cross.target, "cross", pressured, 0.8),
      },
      cross.mate,
    );
  }

  if (
    pass &&
    !pass.backward &&
    pass.score > AI.MIN_PASS_SCORE_NORM * profile.passBias &&
    pass.score > shot.score * passBias
  ) {
    return emit(
      "pase:gana-al-tiro",
      {
        kind: "pass",
        target: pass.target,
        power: pass.power,
        spread: aiPassSpread(
          player,
          pass.target,
          "pass",
          pressured,
          Math.min(dist(player.pos, pass.target) / range, 1),
        ),
      },
      pass.mate,
    );
  }

  // El audaz remata desde donde el conservador ni lo piensa.
  const boldShot = 1 / (1 + (boldness - 1) * AI.BOLDNESS_SHOT_GAIN);
  const minShot =
    ((toGoalLine < close ? AI.MIN_CLOSE_SHOT_SCORE : AI.MIN_SHOT_SCORE) *
      profile.shotThresholdScale *
      boldShot) /
    (mentality.shootBias * profile.shootBias);

  if (shot.score >= minShot) {
    return emit("remate:umbral", finishShot(player, shot, range));
  }

  /*
   * CONSTRUCCION: jugar atras a proposito, sin estar presionado.
   *
   * Esta rama es la que faltaba, y era la razon de que la IA no construyera.
   * TODAS las demas ramas de pase exigian `!pass.backward`, y la unica que lo
   * admitia (`pase:reciclaje-atras`) pedia estar presionado. O sea que un pase
   * atras solo podia ser una HUIDA, nunca una decision — por bien que
   * puntuara. Medido: el 2% de los pases iban hacia atras en todos los modos,
   * incluidos los que tienen `allowBackwardPass`.
   *
   * Solo en los modos que construyen. En 2v2/3v3 tocar hacia atras sigue
   * siendo casi siempre regalar el balon: no hay a quien salir despues.
   *
   * Y con un umbral MAS alto que el pase normal: retrasar el balon tiene que
   * ganarselo, no ser la salida comoda cuando no se ve nada delante.
   */
  const buildUp = profile.allowBackwardPass ? lastBuildUpPass() : null;
  if (
    buildUp &&
    buildUp.score > AI.MIN_PASS_SCORE_NORM * AI.BUILDUP_MIN_SCORE_SCALE * profile.passBias
  ) {
    return emit(
      "construccion:atras",
      {
        kind: "pass",
        target: buildUp.target,
        power: buildUp.power,
        spread: aiPassSpread(player, buildUp.target, "pass", pressured, 1),
      },
      buildUp.mate,
    );
  }

  if (
    pass &&
    !pass.backward &&
    pass.score > AI.MIN_PASS_SCORE_NORM * 0.7 * profile.passBias * mentality.passBias
  ) {
    return emit(
      "pase:relajado",
      {
        kind: "pass",
        target: pass.target,
        power: pass.power,
        spread: aiPassSpread(player, pass.target, "pass", pressured, 1),
      },
      pass.mate,
    );
  }

  // Reciclaje: 11v11 siempre; arcade solo combinacion corta (tiki-taka).
  if (
    pass &&
    pass.backward &&
    pressured &&
    pass.score > AI.MIN_PASS_SCORE_NORM * 0.75 &&
    (profile.allowBackwardPass || dist(player.pos, pass.target) <= AI.SHORT_PASS_MAX)
  ) {
    return emit(
      "pase:reciclaje-atras",
      {
        kind: "pass",
        target: pass.target,
        power: pass.power,
        spread: aiPassSpread(player, pass.target, "pass", pressured, 1),
      },
      pass.mate,
    );
  }

  let nearestDist = Infinity;
  let pressure: Player | null = null;
  for (const o of opponents) {
    if (o.role === "gk") continue;
    const d = dist(o.pos, player.pos);
    if (d < nearestDist) {
      nearestDist = d;
      pressure = o;
    }
  }

  // Ultimo filtro tiki-taka: si hay cualquier pase decente, circular > regatear.
  if (
    pass &&
    pass.score > AI.MIN_PASS_SCORE_NORM * 0.6 * profile.passBias * mentality.passBias &&
    (!pressure || nearestDist > AI.PRESSURE_RADIUS * 0.55 || pass.score > AI.MIN_PASS_SCORE_NORM * 1.2)
  ) {
    return emit(
      "tiki-taka:ultimo-filtro",
      {
        kind: "pass",
        target: pass.target,
        power: pass.power,
        spread: aiPassSpread(player, pass.target, "pass", pressured, 1),
      },
      pass.mate,
    );
  }

  const dribbleTarget = findOpenSpace(player, ball, mates, opponents, goal, dir, standoff);
  if (pressure && nearestDist < AI.PRESSURE_RADIUS * 2) {
    dribbleTarget.y = clamp(
      player.pos.y + (player.pos.y >= pressure.pos.y ? 1 : -1) * 140,
      player.radius,
      FIELD.HEIGHT - player.radius,
    );
    // Si ya esta en rango y le aprietan, el dribbling largo es suicide: chuta.
    if (toGoalLine < range * 0.85 && shot.score > 20) {
      return emit("remate:acorralado", finishShot(player, shot, range));
    }
  }

  const wide = crossBeforeRecycle();
  if (wide) return wide;
  const driving = driveOn();
  if (driving) return driving;
  const recycled = recycle();
  if (recycled) return recycled;
  if (shield) return emit("aguanta:de-espaldas", { kind: "dribble", target: shield, power: 0, spread: 0 });

  return emit("conduce", {
    kind: "dribble",
    target: dribbleTarget,
    power: 0,
    spread: 0,
  });
}

/**
 * Centro "con esperanza" desde el fondo (`AI.CROSS_BYLINE_DEPTH`): al
 * compañero MAS LIBRE de los que estan dentro del area, a medio camino entre
 * el y el segundo palo, aunque `evaluateCross` no vea un centro limpio (pide
 * un timing casi perfecto). Puntuacion 0: no compite con nada, solo sale
 * cuando se llega al fondo. `null` si el area esta vacia.
 */
function hopefulCross(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): CrossEval | null {
  let target: Player | null = null;
  let spot: Vec2 | null = null;
  let room = -1;
  for (const mate of mates) {
    if (mate.role === "gk") continue;
    const ahead = arrivingAt(mate);
    if (!inAttackingBox(mate) && !pointInAttackingBox(ahead, mate.side)) continue;
    const free = nearestOpponentDist(ahead, opponents);
    if (free > room) {
      room = free;
      target = mate;
      spot = ahead;
    }
  }
  if (!target || !spot) return null;
  const farPost = defaultCrossTarget(player.side, player.pos.y);
  const landing = vec((farPost.x + spot.x) / 2, (farPost.y + spot.y) / 2);
  return {
    score: 0,
    target: landing,
    mate: target,
    power: clamp(dist(ball.pos, landing) / 780, 0.55, 0.98),
    timingError: AI.CROSS_TIMING_WINDOW,
  };
}

/** ¿Ya va lanzado hacia ese punto? (`AI.DRIVE_MIN_SPEED`, `AI.DRIVE_HEADING`) */
function headingTo(player: Player, aim: Vec2): boolean {
  const speed = len2(player.vel.x, player.vel.y);
  if (speed < AI.DRIVE_MIN_SPEED) return false;
  const dx = aim.x - player.pos.x;
  const dy = aim.y - player.pos.y;
  const d = len2(dx, dy) || 1;
  return (player.vel.x * dx + player.vel.y * dy) / (speed * d) >= AI.DRIVE_HEADING;
}

/**
 * Desborde por banda (`AI.DRIVE_*`): el siguiente punto hacia la linea de
 * fondo por su banda, o `null` si no toca. Solo extremos y laterales, en modos
 * con bandas, pegados a la linea, en campo rival y con la banda libre delante.
 */
export function wingDriveTarget(player: Player, opponents: readonly Player[]): Vec2 | null {
  if (player.tacticRole !== "wm" && player.tacticRole !== "fb") return null;
  if (fieldPlayersPerTeam() < TACTICS.FLANK_MIN_OUTFIELD) return null;
  const goal = attackedGoal(player.side);
  const toGoalLine = Math.abs(goal.x - player.pos.x);
  if (toGoalLine > AI.DRIVE_MAX_DEPTH || toGoalLine < AI.DRIVE_MIN_DEPTH) return null;
  const off = player.pos.y - HALF_HEIGHT;
  if (Math.abs(off) < AI.DRIVE_WIDE) return null;
  // Hacia el carril de la zona de centro, sin irse contra la linea.
  const bandY = off < 0 ? KICK.CROSS_WING_BAND / 2 : FIELD.HEIGHT - KICK.CROSS_WING_BAND / 2;
  const step = Math.min(AI.DRIVE_STEP, toGoalLine - AI.DRIVE_MIN_DEPTH * 0.5);
  const aim = vec(
    player.pos.x + attackDir(player.side) * step,
    player.pos.y + clamp(bandY - player.pos.y, -step * 0.4, step * 0.4),
  );
  const field = opponents.filter((o) => o.role !== "gk");
  return laneClearanceAhead(player.pos, aim, field) >= AI.DRIVE_LANE ? aim : null;
}

/**
 * Aguantar de espaldas (`AI.SHIELD_*`): a donde ir para cubrir el balon, o
 * `null` si no toca. Se aleja del rival que aprieta de frente, un poco hacia el
 * compañero que viene, con lo que el balon —que va pegado por delante— queda
 * al otro lado del cuerpo.
 *
 * No en el ultimo tercio (`AI.SHIELD_MAX_ADVANCE`) ni cerca del area
 * (`nearGoal`): ahi, con alguien encima, se encara o se remata, no se espera.
 */
export function shieldTarget(
  player: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
  holdTime: number,
  nearGoal: boolean,
): Vec2 | null {
  if (nearGoal || holdTime >= AI.SHIELD_MAX_HOLD) return null;
  const advance = player.side === "home" ? player.pos.x / FIELD.WIDTH : 1 - player.pos.x / FIELD.WIDTH;
  if (advance > AI.SHIELD_MAX_ADVANCE) return null;
  let presser: Player | null = null;
  let pressDist: number = AI.SHIELD_PRESSURE;
  for (const o of opponents) {
    if (o.role === "gk") continue;
    const d = dist(o.pos, player.pos);
    if (d < pressDist) {
      pressDist = d;
      presser = o;
    }
  }
  if (!presser || pressDist < 1e-3) return null;
  const dir = attackDir(player.side);
  if (((presser.pos.x - player.pos.x) * dir) / pressDist < AI.SHIELD_AHEAD_COS) return null;

  let support: Player | null = null;
  let supportDist: number = AI.SHIELD_SUPPORT;
  for (const m of mates) {
    if (m.role === "gk") continue;
    const d = dist(m.pos, player.pos);
    if (d < supportDist) {
      supportDist = d;
      support = m;
    }
  }
  if (!support) return null;

  // Lejos del rival, torcido hacia el compañero (sin pasarse de el).
  let ax = (player.pos.x - presser.pos.x) / pressDist;
  let ay = (player.pos.y - presser.pos.y) / pressDist;
  ax += ((support.pos.x - player.pos.x) / supportDist) * 0.35;
  ay += ((support.pos.y - player.pos.y) / supportDist) * 0.35;
  const len = len2(ax, ay) || 1;
  return vec(
    clamp(player.pos.x + (ax / len) * AI.SHIELD_STEP, player.radius, FIELD.WIDTH - player.radius),
    clamp(player.pos.y + (ay / len) * AI.SHIELD_STEP, player.radius, FIELD.HEIGHT - player.radius),
  );
}

function finishShot(
  player: Player,
  shot: ShotEval,
  range: number,
): AttackChoice {
  const fatigue = 1 - clamp(player.stamina, 0, 1);
  const spread =
    AI.SHOT_SPREAD *
    Math.min(dist(player.pos, shot.target) / range, 1) *
    (1 - clamp(shot.clearance / (AI.LANE_CLEARANCE * 2), 0, 0.6)) *
    (ACTIVE_MATCH_MODE.ai.shotThresholdScale < 0.8 ? 0.75 : 1) *
    (1 + fatigue * 0.55) *
    (shot.style === "placed" ? 0.55 : shot.style === "chip" ? 0.7 : 1);
  return {
    kind: "shoot",
    target: shot.target,
    power: shot.power,
    spread,
    shotStyle: shot.style,
  };
}

/** Busca la zona con menos rivales para conducir (vision espacial). */
function findOpenSpace(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
  goal: Vec2,
  dir: number,
  standoff: number,
): Vec2 {
  let best = vec(goal.x - dir * standoff, goal.y);
  let bestScore = -Infinity;
  const step = ACTIVE_MATCH_MODE.ai.allowBackwardPass ? 120 : 150;
  const pressured = opponents.some(
    (o) => o.role !== "gk" && dist(o.pos, player.pos) < AI.PRESSURE_RADIUS * 1.6,
  );
  const fieldMates = mates.filter((m) => m.role !== "gk");
  const fieldOpponents = opponents.filter((o) => o.role !== "gk");

  for (let i = 0; i < 5; i++) {
    const t = (i / 4) * 2 - 1;
    let candidate = vec(
      player.pos.x + dir * step,
      clamp(HALF_HEIGHT + t * FIELD.HEIGHT * 0.38, player.radius, FIELD.HEIGHT - player.radius),
    );

    if (pressured && Math.abs(t) > 0.4) {
      const band = t < 0 ? player.radius + 28 : FIELD.HEIGHT - player.radius - 28;
      candidate = vec(candidate.x, band);
    }

    const offMul = offsideSpaceScore(
      candidate.x,
      player.side,
      ball.pos.x,
      opponents,
      fieldMates,
    );
    if (offMul <= 0) continue;

    let space = Infinity;
    for (const o of fieldOpponents) {
      space = Math.min(space, dist(candidate, o.pos));
    }
    const forward = (candidate.x - player.pos.x) * dir;
    const toGoal = dist(candidate, goal);
    const wallHug =
      Math.min(candidate.y, FIELD.HEIGHT - candidate.y) < 70 && pressured ? 35 : 0;
    const score = (space + forward * 0.25 - toGoal * 0.08 + wallHug) * offMul;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

export function scatterDir(dir: Vec2, spread: number, random: Random): void {
  if (spread <= 0) return;
  const angle = datan2(dir.y, dir.x) + signed(random) * spread;
  dir.x = dcos(angle);
  dir.y = dsin(angle);
}
